import {Descriptions} from 'antd'
import {colors} from 'helpers/style'
import {toF} from 'helpers/temperatures'
import React from 'react'

const formatFC = c => {
  if (typeof c === 'number' && !isNaN(c)) {
    return `${toF(c).toFixed(1)}°F / ${c.toFixed(1)}°C`
  }
  return '--'
}
/**
 * If interval is null, display only the date and time (not as a range bar).
 * Otherwise, display the range bar.
 */
const RangeOrSingleTime = ({timestamp, rangeEnd, interval}) => {
  if (!interval || !interval.value) {
    // Show only single date and time
    const d = new Date(timestamp)
    return (
      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: 16}}>
        <span style={{fontWeight: 500, fontSize: 13}}>{d.toLocaleTimeString()}</span>
        <span style={{fontSize: 12, color: '#888'}}>{d.toLocaleDateString()}</span>
      </div>
    )
  } else {
    // Show the range bar (copied from below)
    const startDate = new Date(timestamp).toLocaleDateString()
    const endDate = new Date(rangeEnd).toLocaleDateString()
    const startTime = new Date(timestamp).toLocaleTimeString()
    const endTime = new Date(rangeEnd).toLocaleTimeString()
    const crossesMidnight = startDate !== endDate
    return (
      <div style={{display: 'flex', alignItems: 'center', marginBottom: 16}}>
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 110}}>
          <span style={{fontWeight: 500, fontSize: 13}}>{startTime}</span>
          <span style={{fontSize: 12, color: '#888'}}>{startDate}</span>
        </div>
        <div
          style={{
            flex: 1,
            height: 6,
            background: 'linear-gradient(90deg, #4f8cff 0%, #a0c4ff 100%)',
            margin: '0 10px',
            borderRadius: 3
          }}
        />
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 110}}>
          <span style={{fontWeight: 500, fontSize: 13}}>{endTime}</span>
          {crossesMidnight && <span style={{fontSize: 12, color: '#888'}}>{endDate}</span>}
        </div>
      </div>
    )
  }
}

const TemperatureDetails = ({temperature, interval}) => {
  if (!temperature) return <></>

  // Calculate range start and end
  const timestamp = temperature.timestamp ? new Date(temperature.timestamp) : null
  let rangeEnd = null
  if (timestamp && interval && interval.value) {
    rangeEnd = new Date(timestamp.getTime() + interval.value * 60000)
  }

  // Graphical range bar component
  const RangeBar = () => {
    if (!rangeEnd || !timestamp) return null
    const startDate = timestamp.toLocaleDateString()
    const endDate = rangeEnd.toLocaleDateString()
    const startTime = timestamp.toLocaleTimeString()
    const endTime = rangeEnd.toLocaleTimeString()
    const crossesMidnight = startDate !== endDate
    return (
      <div style={{display: 'flex', alignItems: 'center', marginBottom: 16}}>
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 110}}>
          <span style={{fontWeight: 500, fontSize: 13}}>{startTime}</span>
          <span style={{fontSize: 12, color: '#888'}}>{startDate}</span>
        </div>
        <div
          style={{
            flex: 1,
            height: 6,
            background: 'linear-gradient(90deg, #4f8cff 0%, #a0c4ff 100%)',
            margin: '0 10px',
            borderRadius: 3
          }}
        />
        <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 110}}>
          <span style={{fontWeight: 500, fontSize: 13}}>{endTime}</span>
          {crossesMidnight && <span style={{fontSize: 12, color: '#888'}}>{endDate}</span>}
        </div>
      </div>
    )
  }

  // Defensive helpers for humidity and pressure
  const safeHumidity =
    typeof temperature.humidity === 'number' && !isNaN(temperature.humidity) ? `${temperature.humidity}%` : '--'
  const safePressure =
    typeof temperature.pressure === 'number' && !isNaN(temperature.pressure) ? `${temperature.pressure} hPa` : '--'

  return (
    <>
      <RangeBar />
      <Descriptions
        column={1}
        bordered={false}
        size="small"
        labelStyle={{
          fontWeight: 'bold',
          color: 'gray',
          whiteSpace: 'nowrap',
          width: '10rem',
          verticalAlign: 'top'
        }}
        contentStyle={{
          fontWeight: 'bold',
          color: 'black',
          margin: 0,
          padding: 0,
          whiteSpace: 'normal'
        }}
      >
        {/* Graphical range bar is above */}
        <Descriptions.Item label="Maximum">
          <span style={{color: colors.chart.maximum}}>{formatFC(temperature.max)}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Median">
          <span style={{color: colors.chart.median}}>{formatFC(temperature.median)}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Mean">
          <span style={{color: colors.chart.mean}}>{formatFC(temperature.mean)}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Minimum">
          <span style={{color: colors.chart.minimum}}>{formatFC(temperature.min)}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Ambient">
          <span style={{color: colors.chart.ambient}}>{formatFC(temperature.ambient)}</span>
        </Descriptions.Item>
        <Descriptions.Item label="Humidity">{safeHumidity}</Descriptions.Item>
        <Descriptions.Item label="Pressure">{safePressure}</Descriptions.Item>
      </Descriptions>
    </>
  )
}

export default TemperatureDetails
